// Prefetch warmer for offline clock-in/clock-out.
//
// Fires the same GETs the clock modals make so their responses land in the
// offline read-through cache (utils/clockDataCache.js) while the device is
// still online. Called by TopNavbar whenever a current shift is found and
// again right after a successful online clock-in — the moment we know a
// clock-out (the request most likely to happen in a dead zone) is coming.
//
// All requests are fire-and-forget best-effort: a failure just means that
// piece won't be available offline.

import { serviceCodesService } from "@/serviceHandlers/serviceCodesService";
import { nonServiceCodesService } from "@/serviceHandlers/nonServiceCodesService";
import { vrActivitiesService } from "@/serviceHandlers/vrActivitiesService";
import { shiftNoteQuestionsService } from "@/serviceHandlers/shiftNoteQuestionsService";
import { clientsService } from "@/serviceHandlers/clientsService";
import { jobCoachingActivitiesService } from "@/serviceHandlers/jobCoachingActivitiesService";
import { caregiverComplianceService } from "@/serviceHandlers/caregiverComplianceService";
import { documentAcknowledgmentsService } from "@/serviceHandlers/documentAcknowledgmentsService";

// Codes whose activity pickers don't use the generic vr_activities lists
// (mirrors mixins/vrActivitiesMixin.js).
const JD_CODES = ["CE", "SE", "SJBT", "TWE"];
const JC_CODE = "JC";

const PREFETCH_TTL_MS = 10 * 60 * 1000;
const lastPrefetchAt = {};

/**
 * Warm the offline cache with everything needed to clock in AND out of the
 * given shift. Throttled per shift id. Safe to call opportunistically.
 * @returns {Promise<boolean>} true if a prefetch actually ran
 */
export async function prefetchShiftClockData(shift, userId) {
  if (!shift || !navigator.onLine) return false;
  const key = String(shift.id || "unknown");
  const now = Date.now();
  if (lastPrefetchAt[key] && now - lastPrefetchAt[key] < PREFETCH_TTL_MS) {
    return false;
  }
  lastPrefetchAt[key] = now;

  const tasks = [];
  const serviceCodes = shift.service_codes || [];
  const clients = shift.clients || [];
  const isVR = shift.payer === "VR";
  const isDSPD = shift.payer === "DSPD";
  const isGroup = shift.shift_type === "Group";

  // Reference lists the modals always consult.
  tasks.push(serviceCodesService.listVR());
  tasks.push(nonServiceCodesService.list());

  for (const sc of serviceCodes) {
    // Admin-configured clock-out questions exist for DSPD and VR codes.
    if (isDSPD || isVR) {
      tasks.push(shiftNoteQuestionsService.getByServiceCode(sc.code));
    }
    if (isVR && sc.code !== JC_CODE && !JD_CODES.includes(sc.code)) {
      tasks.push(vrActivitiesService.listByServiceCode(sc.code));
    }
    if (isVR && sc.code === JC_CODE) {
      tasks.push(jobCoachingActivitiesService.list());
    }
  }

  // Per-client data (medications/goals are DSPD 1-on-1 concerns).
  if (isDSPD && !isGroup) {
    for (const client of clients) {
      tasks.push(clientsService.getMedications(client.id));
      tasks.push(clientsService.getGoals(client.id));
    }
  }

  // Clock-in pre-flight checks, so an offline clock-in can consult the last
  // known compliance answer instead of skipping the gate entirely.
  if (userId && clients.length > 0) {
    tasks.push(caregiverComplianceService.checkClockIn(userId, clients[0].id));
    tasks.push(
      documentAcknowledgmentsService.checkClockInRequirements(
        userId,
        clients[0].id,
      ),
    );
  }

  await Promise.allSettled(tasks);
  return true;
}
